import React from "react";
import { ConversationComment } from "services/ConversationService";
import { UserWithFullName } from "components/Signals/Dashboard/DashboardMenu/ShareDialog/constants";
import { AcxMainTextFieldRef } from "components/UI/AcxMainTextField";
import EditComposer from "./EditComposer";
import MentionDropdown from "./MentionDropdown";
import useMentions from "./useMentions";
import { applyMentionSelection } from "./mentionUtils";

type EditComposerContainerProps = {
    comment: ConversationComment;
    isActive: boolean;
    users: UserWithFullName[] | null | undefined;
    usersLoading: boolean;
    onCancelEdit: () => void;
    onSaveEdit: (
        commentId: string,
        commentText: string,
        taggedUserIds: string[],
    ) => Promise<void>;
    tokenizeMentions: (
        commentText: string,
        selections: UserWithFullName[],
    ) => string;
    detokenizeMentions: (commentText: string) => string;
    getTaggedUserIdsFromComment: (commentText: string) => string[];
};

const EditComposerContainer = ({
    comment,
    isActive,
    users,
    usersLoading,
    onCancelEdit,
    onSaveEdit,
    tokenizeMentions,
    detokenizeMentions,
    getTaggedUserIdsFromComment,
}: EditComposerContainerProps) => {
    const [editDraft, setEditDraft] = React.useState("");
    const [isEditSaving, setIsEditSaving] = React.useState(false);
    const editInputRef = React.useRef<AcxMainTextFieldRef>(null);
    const pendingEditCursorPositionRef = React.useRef<number | null>(null);
    const userLookup = React.useMemo(() => {
        if (!users) return new Map<string, UserWithFullName>();
        return new Map(
            users
                .filter((user) => user.userId)
                .map((user) => [user.userId, user]),
        );
    }, [users]);

    const getMentionSelectionsFromTokens = React.useCallback(
        (commentText: string) => {
            const tokenRegex = /@\[([^|\]]+)\|([^\]]+)\]/g;
            const selections: UserWithFullName[] = [];
            const seen = new Set<string>();
            for (const match of commentText.matchAll(tokenRegex)) {
                const userId = match[1];
                const displayName = match[2];
                if (!userId || seen.has(userId)) continue;
                const user = userLookup.get(userId);
                if (user) {
                    selections.push(user);
                } else if (displayName) {
                    selections.push({
                        userId,
                        fullName: displayName,
                        email: "",
                        firstName: "",
                        lastName: "",
                        userName: "",
                    });
                }
                seen.add(userId);
            }
            return selections;
        },
        [userLookup],
    );

    const {
        state: {
            matchingUsers: editMatchingUsers,
            mentionSelections: editMentionSelections,
            mentionStartIndex: editMentionStartIndex,
            mentionDropdownPosition: editMentionDropdownPosition,
            activeMentionIndex: editMentionActiveIndex,
            shouldShowMentions: editShouldShowMentions,
        },
        actions: {
            setMentionQuery: setEditMentionQuery,
            setMentionStartIndex: setEditMentionStartIndex,
            setMentionSelections: setEditMentionSelections,
            setActiveMentionIndex: setEditMentionActiveIndex,
            setCursorPosition: setEditCursorPosition,
            syncCursorFromEvent: syncEditCursorFromEvent,
            handleMentionKeyDown: handleEditMentionKeyDown,
        },
        refs: { mentionDropdownRef: editMentionDropdownRef },
    } = useMentions({
        users,
        usersLoading,
        inputRef: editInputRef,
        inputValue: editDraft,
    });

    const editMentionListboxId = comment.id
        ? `edit-mention-listbox-${comment.id}`
        : "edit-mention-listbox";
    const getEditMentionOptionId = React.useCallback(
        (user: UserWithFullName, index: number) =>
            `edit-mention-option-${comment.id ?? "comment"}-${
                user.userId ?? index
            }`,
        [comment.id],
    );

    React.useEffect(() => {
        if (!isActive) return;
        const resolvedText = detokenizeMentions(comment.comment);
        setEditDraft(resolvedText);
        setEditMentionSelections(
            getMentionSelectionsFromTokens(comment.comment),
        );
        setEditMentionQuery("");
        setEditMentionStartIndex(null);
        setEditCursorPosition(resolvedText.length);
        pendingEditCursorPositionRef.current = resolvedText.length;
    }, [
        comment.comment,
        detokenizeMentions,
        isActive,
        setEditCursorPosition,
        setEditMentionQuery,
        setEditMentionSelections,
        setEditMentionStartIndex,
        getMentionSelectionsFromTokens,
    ]);

    React.useEffect(() => {
        const pendingCursorPosition = pendingEditCursorPositionRef.current;
        if (pendingCursorPosition === null) return;
        const timer = window.setTimeout(() => {
            const input = editInputRef.current;
            if (!input) return;
            input.focus?.();
            if ("setSelectionRange" in input) {
                input.setSelectionRange(
                    pendingCursorPosition,
                    pendingCursorPosition,
                );
            }
            pendingEditCursorPositionRef.current = null;
        }, 0);
        return () => window.clearTimeout(timer);
    }, [editDraft]);

    const handleSelectEditMentionUser = React.useCallback(
        (user: UserWithFullName) => {
            applyMentionSelection(user, {
                value: editDraft,
                mentionStartIndex: editMentionStartIndex,
                setValue: setEditDraft,
                setCursorPosition: setEditCursorPosition,
                pendingCursorPositionRef: pendingEditCursorPositionRef,
                setMentionQuery: setEditMentionQuery,
                setMentionStartIndex: setEditMentionStartIndex,
                setMentionSelections: setEditMentionSelections,
            });
        },
        [
            editDraft,
            editMentionStartIndex,
            setEditCursorPosition,
            setEditMentionQuery,
            setEditMentionSelections,
            setEditMentionStartIndex,
        ],
    );

    const handleSaveEdit = React.useCallback(async () => {
        if (!comment.id) return;
        const trimmed = editDraft.trim();
        if (!trimmed || isEditSaving) return;

        const tokenizedComment = tokenizeMentions(
            editDraft,
            editMentionSelections,
        );
        const resolvedTaggedUserIds =
            getTaggedUserIdsFromComment(tokenizedComment);

        setIsEditSaving(true);
        try {
            await onSaveEdit(
                comment.id,
                tokenizedComment.trim(),
                resolvedTaggedUserIds,
            );
        } finally {
            setIsEditSaving(false);
        }
    }, [
        comment.id,
        editDraft,
        editMentionSelections,
        getTaggedUserIdsFromComment,
        isEditSaving,
        onSaveEdit,
        tokenizeMentions,
    ]);

    if (!isActive) return null;

    return (
        <EditComposer
            value={editDraft}
            isSaving={isEditSaving}
            inputRef={editInputRef}
            onChange={(event) => {
                setEditDraft(event.target.value);
                syncEditCursorFromEvent(event);
            }}
            inputProps={{
                onClick: syncEditCursorFromEvent,
                onKeyUp: syncEditCursorFromEvent,
                onKeyDown: (event) =>
                    handleEditMentionKeyDown(
                        event,
                        handleSelectEditMentionUser,
                    ),
                onBlur: () => {
                    setEditMentionQuery("");
                    setEditMentionStartIndex(null);
                },
                role: "combobox",
                "aria-autocomplete": "list",
                "aria-expanded": editShouldShowMentions,
                "aria-controls": editShouldShowMentions
                    ? editMentionListboxId
                    : undefined,
                "aria-activedescendant": editShouldShowMentions
                    ? getEditMentionOptionId(
                          editMatchingUsers[editMentionActiveIndex],
                          editMentionActiveIndex,
                      )
                    : undefined,
            }}
            onCancel={onCancelEdit}
            onSubmit={handleSaveEdit}
            mentionDropdown={
                <MentionDropdown
                    id={editMentionListboxId}
                    testId="edit-mention-dropdown"
                    position={editMentionDropdownPosition}
                    isOpen={editShouldShowMentions}
                    users={editMatchingUsers}
                    activeIndex={editMentionActiveIndex}
                    dropdownRef={editMentionDropdownRef}
                    getOptionId={getEditMentionOptionId}
                    onHover={setEditMentionActiveIndex}
                    onSelect={handleSelectEditMentionUser}
                />
            }
        />
    );
};

export default EditComposerContainer;
